import React, { useCallback, useMemo, useState } from 'react';
import Text from 'components/Text/Text';
import SearchField from 'components/SearchField/SearchField';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { Checkbox } from 'antd';
import { IItemForGetShortDishInfo } from 'types/dishes';
import style from './MultipleSelect.module.scss';

interface IProps {
	data: IItemForGetShortDishInfo[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setSelectedDishes: (value: string[]) => void;
	selectedDishes: string[];
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({ data, isLoading, refetch, responseError, setSelectedDishes, selectedDishes }) => {
		const [searchString, setSearchString] = useState<string>('');

		const filteredDishes = useMemo(() => {
			if (!searchString) {
				return data;
			}

			const search = searchString.toLowerCase();
			return data.filter(
				(el) => el.name.toLowerCase().includes(search) || el.externalId?.toLowerCase().includes(search),
			);
		}, [searchString, data]);

		const handleSelectAllDishes = useCallback(() => {
			if (data.length === selectedDishes.length) {
				setSelectedDishes([]);
			} else {
				const IDsOfAllDishes = data.map((el) => el.id);
				setSelectedDishes(IDsOfAllDishes);
			}
		}, [setSelectedDishes, data, selectedDishes]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				const getIsCheckedByHasAccess = () => {
					return !!(data.length && data.length === selectedDishes.length);
				};

				return (
					<>
						<div className={style.selectMenuHeader}>
							<Checkbox
								className={style.checkBox}
								checked={getIsCheckedByHasAccess()}
								disabled={!data.length}
								onChange={handleSelectAllDishes}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllDishesLabel"
									asFragment={true}
								/>
							</Checkbox>
							<SearchField
								onChangeSelectSearchString={setSearchString}
								searchString={searchString}
								className={style.searchField}
							/>
						</div>
						{menu}
					</>
				);
			},
			[data, selectedDishes, handleSelectAllDishes, setSearchString, searchString],
		);

		return (
			<OutsideFormSelect
				testId="MultipleSelect"
				name="selectedDishes"
				label="dishesLabel"
				value={selectedDishes}
				options={filteredDishes}
				fetchError={responseError}
				loading={isLoading}
				onRefetch={refetch}
				disabled={isLoading}
				onChange={setSelectedDishes}
				counterValue={selectedDishes.length}
				withValidation={false}
				dropdownRender={renderSelectMenu}
				mode="multiple"
			/>
		);
	},
);
